/**
 * PURPOSE: Punto de entrada global del Master de oficiales de partido (FTR-0001), solo rol 1.
 * RESPONSIBILITIES: Permitir a un usuario rol 1 (no necesariamente ligado a una sola compañía)
 *   elegir la compañía objetivo, reutilizando el mismo patrón de selector que
 *   AdminCompanySportsTab.jsx, y delegar el CRUD completo a CompanyOfficialsTab.jsx.
 * COLLABORATORS: adminCompanyService.js, CompanyOfficialsTab.jsx, AdminSection.jsx.
 * ROUTE: winscore/src/admin/AdminCompany/AdminOfficialsTab.jsx
 */
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import { getApiErrorMessage, listCompanies } from "../../services/adminCompanyService.js";
import CompanyOfficialsTab from "./CompanyOfficialsTab.jsx";

const AdminOfficialsTab = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  useEffect(() => {
    let active = true;

    const loadCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const items = await listCompanies();
        if (!active) return;

        const sortedCompanies = [...items].sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), "es")
        );
        setCompanies(sortedCompanies);
        setSelectedCompanyId((current) =>
          current || (sortedCompanies[0]?.idcompany ? String(sortedCompanies[0].idcompany) : "")
        );
      } catch (error) {
        console.error("Error al cargar compañías", error);
        toast.error(getApiErrorMessage(error, "No se pudieron cargar las compañías."));
      } finally {
        if (active) setLoadingCompanies(false);
      }
    };

    loadCompanies();

    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-600 dark:bg-gray-700">
        <div className="mb-4">
          <h2 className="text-xl font-bold text-slate-800 dark:text-white">Oficiales de partido</h2>
          <p className="text-sm text-slate-500 dark:text-slate-300">
            Selecciona una compañía para administrar su catálogo de oficiales (árbitros y demás roles) y su
            elegibilidad por rol.
          </p>
        </div>

        <label className="block max-w-2xl text-sm font-medium text-slate-700 dark:text-slate-200">
          Compañía
          <select
            value={selectedCompanyId}
            onChange={(event) => setSelectedCompanyId(event.target.value)}
            disabled={loadingCompanies}
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-slate-100"
          >
            <option value="">
              {loadingCompanies ? "Cargando compañías..." : "Seleccione una compañía"}
            </option>
            {companies.map((company) => (
              <option key={company.idcompany} value={company.idcompany}>
                {company.name}
                {company.abrev ? ` (${company.abrev})` : ""}
              </option>
            ))}
          </select>
        </label>
      </section>

      {!selectedCompanyId ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-5 py-10 text-center text-sm text-slate-500">
          Selecciona una compañía para continuar.
        </div>
      ) : (
        // key forces a full remount on company change, so no officials/state from the previous
        // company can leak into the next one (same pattern as AdminCompanySportsTab.jsx).
        <CompanyOfficialsTab key={selectedCompanyId} idcompany={Number(selectedCompanyId)} />
      )}
    </div>
  );
};

export default AdminOfficialsTab;
